import React, { useMemo } from 'react';
import { Users, Package, CreditCard, AlertCircle, DollarSign, Tag, TicketPercent } from 'lucide-react';
import { Heading } from '../catalyst/heading';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../catalyst/table';
import { Text } from '../catalyst/text';
import StatCard from '../components/StatCard';
import ResourceLink from '../components/ResourceLink';
import { OrderStatusBadge, CodeStatusBadge } from '../components/StatusBadge';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import type { ConversationMessage, OrderStatus, PaymentIntent, CodeStatus } from '../types/database';

const STATUS_COLORS: Record<OrderStatus, string> = {
	DRAFT: 'bg-zinc-400',
	IN_REVIEW: 'bg-amber-400',
	PENDING_PAYMENT: 'bg-orange-400',
	PAID: 'bg-green-400',
	FULFILLING: 'bg-blue-400',
	SHIPPED: 'bg-indigo-400',
	DELIVERED: 'bg-emerald-400',
	CANCELLED: 'bg-red-400',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
	DRAFT: 'Draft',
	IN_REVIEW: 'In Review',
	PENDING_PAYMENT: 'Pending Payment',
	PAID: 'Paid',
	FULFILLING: 'Fulfilling',
	SHIPPED: 'Shipped',
	DELIVERED: 'Delivered',
	CANCELLED: 'Cancelled',
};

function useCountQuery(table: string, filter?: (q: any) => any) {
	return useSupabaseQuery<never>(
		(client) => {
			let q = client.from(table).select('*', { count: 'exact', head: true });
			if (filter) q = filter(q);
			return q;
		},
		[table]
	);
}

const DashboardPage: React.FC = () => {
	// KPI queries
	const { count: userCount, loading: usersLoading } = useCountQuery('users');
	const { count: orderCount, loading: ordersLoading } = useCountQuery('orders');
	const { count: paidCount, loading: paidLoading } = useCountQuery('orders', (q: any) =>
		q.in('status', ['PAID', 'FULFILLING', 'SHIPPED', 'DELIVERED'])
	);

	// Needs Attention: fetch last 500 messages, deduplicate by user_id, count where latest is inbound
	const { data: attentionMessages, loading: attentionLoading } = useSupabaseQuery<ConversationMessage>(
		(client) =>
			client
				.from('conversation_messages')
				.select('id, user_id, direction, created_at')
				.order('created_at', { ascending: false })
				.limit(500),
		[]
	);

	const needsAttentionCount = useMemo(() => {
		const latestByUser = new Map<string, ConversationMessage>();
		for (const msg of attentionMessages) {
			if (!latestByUser.has(msg.user_id)) {
				latestByUser.set(msg.user_id, msg);
			}
		}
		let count = 0;
		for (const msg of Array.from(latestByUser.values())) {
			if (msg.direction === 'INBOUND') count++;
		}
		return count;
	}, [attentionMessages]);

	// Revenue
	const { data: succeededPayments, loading: revenueLoading } = useSupabaseQuery<PaymentIntent>(
		(client) =>
			client
				.from('payment_intents')
				.select('amount, currency')
				.eq('status', 'SUCCEEDED'),
		[]
	);

	// Active codes count
	const { count: activeCodesCount, loading: activeCodesLoading } = useCountQuery('codes', (q: any) => q.eq('status', 'active'));

	// Discounts given
	const { data: discountRows, loading: discountsLoading } = useSupabaseQuery<{ discount_cash_cents: number }>(
		(client) => client.from('orders').select('discount_cash_cents').gt('discount_cash_cents', 0),
		[]
	);

	const totalDiscountGiven = useMemo(
		() => discountRows.reduce((sum, r) => sum + (r.discount_cash_cents || 0), 0) / 100,
		[discountRows]
	);

	const revenueDisplay = useMemo(() => {
		if (succeededPayments.length === 0) {
			return { value: new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(0), subtitle: undefined };
		}
		// Group by currency
		const byCurrency = new Map<string, number>();
		for (const p of succeededPayments) {
			const cur = (p.currency || 'eur').toLowerCase();
			byCurrency.set(cur, (byCurrency.get(cur) || 0) + p.amount);
		}
		// Sort by total descending
		const sorted = Array.from(byCurrency.entries()).sort((a, b) => b[1] - a[1]);
		const [primaryCurrency, primaryAmount] = sorted[0];
		const formatted = new Intl.NumberFormat(undefined, {
			style: 'currency',
			currency: primaryCurrency.toUpperCase(),
		}).format(primaryAmount / 100);

		let subtitle: string | undefined;
		if (sorted.length > 1) {
			const others = sorted.slice(1).map(([cur, amt]) =>
				new Intl.NumberFormat(undefined, {
					style: 'currency',
					currency: cur.toUpperCase(),
				}).format(amt / 100)
			);
			subtitle = `+ ${others.join(', ')}`;
		}

		return { value: formatted, subtitle };
	}, [succeededPayments]);

	// Conversion rate subtitle for Paid Orders
	const conversionSubtitle = useMemo(() => {
		const paid = paidCount ?? 0;
		const total = orderCount ?? 0;
		if (total === 0) return '0 of 0 — 0%';
		const rate = Math.round((paid / total) * 100);
		return `${paid} of ${total} — ${rate}%`;
	}, [paidCount, orderCount]);

	// Orders with status for breakdown bar
	const { data: allOrderStatuses, loading: statusBreakdownLoading } = useSupabaseQuery<{ id: string; status: OrderStatus }>(
		(client) =>
			client.from('orders').select('id, status'),
		[]
	);

	const statusCounts = useMemo(() => {
		const counts = new Map<OrderStatus, number>();
		for (const o of allOrderStatuses) {
			counts.set(o.status, (counts.get(o.status) || 0) + 1);
		}
		return counts;
	}, [allOrderStatuses]);

	const statusTotal = allOrderStatuses.length;

	// Recent orders with recipient slot
	const { data: recentOrders, loading: recentOrdersLoading } = useSupabaseQuery<{
		id: string;
		user_id: string;
		status: OrderStatus;
		created_at: string;
		users: { display_name: string | null; phone_number: string } | null;
		order_slots: { slot_name: string; value: Record<string, unknown> | null }[];
	}>(
		(client) =>
			client
				.from('orders')
				.select('id, user_id, status, created_at, users(display_name, phone_number), order_slots(slot_name, value)')
				.order('created_at', { ascending: false })
				.limit(10),
		[]
	);

	// Recent inbound messages
	const { data: recentMessages, loading: recentMsgsLoading } = useSupabaseQuery<
		ConversationMessage & { users: { phone_number: string; display_name: string | null } | null }
	>(
		(client) =>
			client
				.from('conversation_messages')
				.select('*, users(phone_number, display_name)')
				.eq('direction', 'INBOUND')
				.order('created_at', { ascending: false })
				.limit(10),
		[]
	);

	// Top codes
	const { data: topCodes, loading: topCodesLoading } = useSupabaseQuery<{
		id: string;
		code: string;
		status: CodeStatus;
		created_at: string;
		users: { display_name: string | null; phone_number: string } | null;
	}>(
		(client) => client
			.from('codes')
			.select('id, code, status, created_at, users!codes_owner_id_fkey(display_name, phone_number)')
			.order('created_at', { ascending: false })
			.limit(5),
		[]
	);

	const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
	const formatTime = (iso: string) =>
		new Date(iso).toLocaleString(undefined, {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});

	const getRecipientName = (slots: { slot_name: string; value: Record<string, unknown> | null }[]): string => {
		const slot = slots.find((s) => s.slot_name === 'recipient_name');
		if (!slot?.value) return '—';
		// value is a JSON object; the actual name is typically stored as a string value
		if (typeof slot.value === 'string') return slot.value;
		if (typeof slot.value === 'object' && slot.value !== null) {
			// Try common patterns: { value: "name" } or direct string representation
			const v = (slot.value as any).value ?? (slot.value as any).name;
			if (typeof v === 'string') return v;
		}
		return '—';
	};

	return (
		<div className="p-6 space-y-6">
			<Heading level={1}>Dashboard</Heading>

			{/* KPI Cards */}
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
				<StatCard
					title="Total Users"
					value={usersLoading ? '…' : (userCount ?? 0)}
					icon={<Users className="h-5 w-5" />}
				/>
				<StatCard
					title="Total Orders"
					value={ordersLoading ? '…' : (orderCount ?? 0)}
					icon={<Package className="h-5 w-5" />}
				/>
				<StatCard
					title="Paid Orders"
					value={paidLoading ? '…' : (paidCount ?? 0)}
					subtitle={paidLoading || ordersLoading ? undefined : conversionSubtitle}
					icon={<CreditCard className="h-5 w-5" />}
				/>
				<StatCard
					title="Needs Attention"
					value={attentionLoading ? '…' : needsAttentionCount}
					subtitle="users awaiting reply"
					icon={<AlertCircle className="h-5 w-5" />}
				/>
				<StatCard
					title="Revenue"
					value={revenueLoading ? '…' : revenueDisplay.value}
					subtitle={revenueLoading ? undefined : revenueDisplay.subtitle}
					icon={<DollarSign className="h-5 w-5" />}
				/>
				<StatCard
					title="Active Codes"
					value={activeCodesLoading ? '…' : (activeCodesCount ?? 0)}
					icon={<Tag className="h-5 w-5" />}
				/>
				<StatCard
					title="Discounts Given"
					value={discountsLoading ? '…' : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(totalDiscountGiven)}
					icon={<TicketPercent className="h-5 w-5" />}
				/>
			</div>

			{/* Order Status Breakdown */}
			{!statusBreakdownLoading && statusTotal > 0 && (
				<div className="rounded-lg border border-zinc-950/5 bg-white p-6 dark:border-white/10 dark:bg-zinc-900">
					<h2 className="mb-4 text-sm font-semibold text-zinc-900 dark:text-white">Order Status Distribution</h2>
					<div className="flex h-4 w-full overflow-hidden rounded-full">
						{(Object.keys(STATUS_COLORS) as OrderStatus[]).map((status) => {
							const count = statusCounts.get(status) || 0;
							if (count === 0) return null;
							const pct = (count / statusTotal) * 100;
							return (
								<div
									key={status}
									className={`${STATUS_COLORS[status]} transition-all`}
									style={{ width: `${pct}%` }}
									title={`${STATUS_LABELS[status]}: ${count}`}
								/>
							);
						})}
					</div>
					<div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
						{(Object.keys(STATUS_COLORS) as OrderStatus[]).map((status) => {
							const count = statusCounts.get(status) || 0;
							if (count === 0) return null;
							return (
								<div key={status} className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
									<span className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_COLORS[status]}`} />
									<span>{STATUS_LABELS[status]}</span>
									<span className="font-medium text-zinc-900 dark:text-white">{count}</span>
								</div>
							);
						})}
					</div>
				</div>
			)}

			{/* Recent Activity Tables */}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				{/* Recent Orders */}
				<div className="rounded-lg border border-zinc-950/5 bg-white dark:border-white/10 dark:bg-zinc-900">
					<div className="border-b border-zinc-950/5 px-6 py-4 dark:border-white/10">
						<h2 className="text-base font-semibold text-zinc-900 dark:text-white">Recent Orders</h2>
					</div>
					{recentOrdersLoading ? (
						<div className="p-6">
							<Text>Loading…</Text>
						</div>
					) : recentOrders.length === 0 ? (
						<div className="p-6">
							<Text>No orders yet.</Text>
						</div>
					) : (
						<Table dense>
							<TableHead>
								<TableRow>
									<TableHeader>ID</TableHeader>
									<TableHeader>User</TableHeader>
									<TableHeader>Recipient</TableHeader>
									<TableHeader>Status</TableHeader>
									<TableHeader>Created</TableHeader>
								</TableRow>
							</TableHead>
							<TableBody>
								{recentOrders.map((order) => (
									<TableRow key={order.id} href={`/orders/${order.id}`}>
										<TableCell>
											<ResourceLink id={order.id} />
										</TableCell>
										<TableCell>
											{order.users?.display_name || order.users?.phone_number || '—'}
										</TableCell>
										<TableCell className="max-w-[120px] truncate">
											{getRecipientName(order.order_slots || [])}
										</TableCell>
										<TableCell>
											<OrderStatusBadge status={order.status} />
										</TableCell>
										<TableCell>{formatDate(order.created_at)}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>

				{/* Recent Messages */}
				<div className="rounded-lg border border-zinc-950/5 bg-white dark:border-white/10 dark:bg-zinc-900">
					<div className="border-b border-zinc-950/5 px-6 py-4 dark:border-white/10">
						<h2 className="text-base font-semibold text-zinc-900 dark:text-white">Recent Messages</h2>
					</div>
					{recentMsgsLoading ? (
						<div className="p-6">
							<Text>Loading…</Text>
						</div>
					) : recentMessages.length === 0 ? (
						<div className="p-6">
							<Text>No messages yet.</Text>
						</div>
					) : (
						<Table dense>
							<TableHead>
								<TableRow>
									<TableHeader>User</TableHeader>
									<TableHeader>Content</TableHeader>
									<TableHeader>Time</TableHeader>
								</TableRow>
							</TableHead>
							<TableBody>
								{recentMessages.map((msg) => (
									<TableRow key={msg.id} href={`/chats/${msg.user_id}`}>
										<TableCell>
											{msg.users?.display_name || msg.users?.phone_number || '—'}
										</TableCell>
										<TableCell className="max-w-[200px] truncate">
											{msg.content
												? msg.content.length > 50
													? msg.content.slice(0, 50) + '…'
													: msg.content
												: '—'}
										</TableCell>
										<TableCell className="whitespace-nowrap">
											{formatTime(msg.created_at)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</div>

			{/* Top Codes */}
			<div className="rounded-lg border border-zinc-950/5 bg-white dark:border-white/10 dark:bg-zinc-900">
				<div className="border-b border-zinc-950/5 px-6 py-4 dark:border-white/10">
					<h2 className="text-base font-semibold text-zinc-900 dark:text-white">Recent Codes</h2>
				</div>
				{topCodesLoading ? (
					<div className="p-6"><Text>Loading…</Text></div>
				) : topCodes.length === 0 ? (
					<div className="p-6"><Text>No codes yet.</Text></div>
				) : (
					<Table dense>
						<TableHead>
							<TableRow>
								<TableHeader>Code</TableHeader>
								<TableHeader>Status</TableHeader>
								<TableHeader>Owner</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{topCodes.map((c) => (
								<TableRow key={c.id} href={`/codes/${c.id}`}>
									<TableCell className="font-mono font-bold text-zinc-900 dark:text-white">{c.code}</TableCell>
									<TableCell><CodeStatusBadge status={c.status} /></TableCell>
									<TableCell>{c.users?.display_name || c.users?.phone_number || '—'}</TableCell>
									<TableCell>{formatDate(c.created_at)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>
		</div>
	);
};

export default DashboardPage;
